import { queryKeys } from '@query/keys'
import { usePaginatedQuery } from '@query/usePaginatedQuery'
import { useQueryClient } from '@tanstack/react-query'
import React, { FC, useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { AliasActionsModal } from '@pages/IntroducingBroker/LinkHub/Links/components/AliasActionsModal'
import { useAliasActions } from '@pages/IntroducingBroker/LinkHub/Links/hooks/useAliasActions'

import { AuthSessionContext } from '@context/AuthContext'
import { useModal } from '@context/ModalsContext'

import { Body2, Title1 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { KebabIcon } from '@ui/icons/Kebab.icon'
import { Pagination } from '@ui/v2/Pagination/Pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNoResults,
  TableRow,
} from '@ui/v2/Table/Table'

import { PageQuery, useApiClient } from '@utils/ApiClient'
import { ClientApiClient, emptyPaginationResponse } from '@utils/clientApi'
import { formatDate } from '@utils/date.utils'
import { useWindowResize } from '@utils/domUtils'

import { ReferralLinkAliasesDto } from '@model/ReferralUrlAliasDto'

import { Box } from '@shared/components/Box'

import styles from './ReferralLinkAliasesTable.module.scss'

interface AliasActionsMenuProps {
  alias: ReferralLinkAliasesDto
  rowNumber: number
  onDelete: (alias: ReferralLinkAliasesDto) => Promise<void>
  ibCode: string
}

interface AliasActionsProps {
  alias: ReferralLinkAliasesDto
  onDelete: (alias: ReferralLinkAliasesDto) => Promise<void>
  ibCode: string
}

interface ReferralLinkAliasesTableProps {
  introducingBrokerId?: string
  mainIbSchemeId: number
  ibCode: string
}

export const ReferralLinkAliasesTable: FC<ReferralLinkAliasesTableProps> = ({
  introducingBrokerId,
  mainIbSchemeId,
  ibCode,
}) => {
  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name
  const isMobile = useWindowResize()

  const query = usePaginatedQuery({
    queryKey: queryKeys.ib.referralLinkAliases(introducingBrokerId, mainIbSchemeId),
    queryFn: (pageQuery) => {
      return introducingBrokerId
        ? apiClient.getReferralLinkAliases(introducingBrokerId, mainIbSchemeId, {
            pageSize: 5,
            ...pageQuery,
          })
        : Promise.resolve(emptyPaginationResponse)
    },
    enabled: !!introducingBrokerId,
  })

  const handlePageChange = (pageIndex: number, pageSize?: number) => {
    query.setPageQuery!((prev: PageQuery) => ({
      ...prev,
      pageIndex,
      ...(pageSize && { pageSize }),
    }))
  }

  const handleDelete = async (alias: ReferralLinkAliasesDto) => {
    await apiClient.updateReferralLinkAlias(alias.id, {
      id: alias.id,
      referralMaterialCategoryId: alias.referralMaterialCategory.id,
      alias: normalizeAlias(alias.alias),
      url: alias.url,
      introducingBrokerId: alias.introducingBroker.id,
      mainIbSchemeId: alias.mainIbScheme.id,
      isDeleted: true,
    })
    queryClient.invalidateQueries({
      queryKey: queryKeys.ib.referralLinkAliases(introducingBrokerId, mainIbSchemeId),
    })
  }

  return (
    <Box flex direction='column' fullWidth>
      <Body2 semibold color='text-high-em'>
        {t('IB.LinkHubPage.Created aliases')}
      </Body2>
      <Table
        isLoading={query.isLoading}
        noResults={
          !query.data.length && (
            <TableNoResults
              renderTitle={() => (
                <Title1 className={styles.noResultsTitle} medium>
                  {t('IB.LinkHubPage.No aliases have been created')}
                </Title1>
              )}
            />
          )
        }
        wrapperClassName={styles.tableWrapper}
        size='sm'
      >
        <TableHeader>
          <TableRow>
            <TableHead width={35}>#</TableHead>
            <TableHead width={255}>{t('IB.LinkHubPage.Alias')}</TableHead>
            <TableHead width={255}>{t('IB.LinkHubPage.Created date')}</TableHead>
            <TableHead width={255}></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {query.data.map((referralLinkAlias, index) => {
            const rowNumber =
              ((query.meta?.pageIndex ?? 1) - 1) * (query.meta?.pageSize ?? 10) + index + 1

            return (
              <TableRow key={referralLinkAlias.id}>
                <TableCell width={35}>{rowNumber}</TableCell>
                <TableCell width={255} hasTruncate>
                  {referralLinkAlias.alias}
                </TableCell>
                <TableCell width={255}>
                  {formatDate(referralLinkAlias.createdDate, {
                    formatType: dateFormat,
                    withTime: false,
                  })}
                </TableCell>
                <TableCell width={255} align='right' hasTruncate={false}>
                  {isMobile ? (
                    <AliasActionsMenu
                      alias={referralLinkAlias}
                      rowNumber={rowNumber}
                      onDelete={handleDelete}
                      ibCode={ibCode}
                    />
                  ) : (
                    <AliasActions
                      alias={referralLinkAlias}
                      onDelete={handleDelete}
                      ibCode={ibCode}
                    />
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      {query.meta && (
        <Pagination
          pageData={query.meta}
          isLoading={query.isLoading}
          onPageChanged={handlePageChange}
          pageSizeOptions={[5]}
          hidePageSize
        />
      )}
    </Box>
  )
}

const AliasActions: FC<AliasActionsProps> = ({ alias, onDelete, ibCode }) => {
  const { actions } = useAliasActions(alias, onDelete, ibCode)

  return (
    <Box flex gap='x8' align='center'>
      {actions.map(({ id, icon, disabled, loading, onClick }) => (
        <IconButton.Ghost
          key={id}
          size='sm'
          icon={icon}
          disabled={disabled || loading}
          onClick={onClick}
        />
      ))}
    </Box>
  )
}

const AliasActionsMenu: FC<AliasActionsMenuProps> = ({ alias, rowNumber, onDelete, ibCode }) => {
  const { actions } = useAliasActions(alias, onDelete, ibCode)
  const aliasActionsModal = useModal(AliasActionsModal, {
    aliasName: alias.alias,
    rowNumber,
    actions,
  })

  return <IconButton.Ghost size='sm' icon={KebabIcon} onClick={() => aliasActionsModal.open()} />
}

const normalizeAlias = (alias: string) => alias.replace(/_/g, ' ')
